import React, {useEffect, useState} from 'react';
import {
    useEnableFolioSubExtensionMutation,
    useGetExtensionFolioQuery, useValidateExtensionFolioMutation
} from '../../store/Api';
import {IInstalledExtensionFolio, IValidateExtensionResponse} from "../../models/Extension";
import {
    ExtensionFolioInstallForm,
    SubExtensionInstallForm
} from "../ExtensionInstallation/ExtensionDataForm";
import {Link, MessageBar, MessageBarType, Spinner} from "@fluentui/react";
import ExtensionValidationErrorModal from "../ExtensionInstallation/ExtensionValidationErrorModal";

interface IGlobalStorefrontOpportunityExtensionForm {
    subExtensionFolioCompositeId?: string;
}

const GlobalStorefrontOpportunityExtensionForm: React.FC<IGlobalStorefrontOpportunityExtensionForm> = (props) => {
    const [folioId, setFolioId] = useState<string>();
    const [subExtensionsId, setSubExtensionId]  = useState<string>();
    const extensionFolioApi = useGetExtensionFolioQuery(folioId ?? '', {skip: (!folioId || folioId == '')})
    const validFolio = useValidateExtensionFolioMutation();
    const [enableFolio] = useEnableFolioSubExtensionMutation()
    const [currentFolio, setCurrentFolio] = useState<IInstalledExtensionFolio | undefined>();
    const [showOpportunityDetails, setShowOpportunityDetails] = useState<boolean>(false);
    const [error, setError] = useState<string | undefined>();
    const [validationResponse, setValidationResponse] = useState<IValidateExtensionResponse | undefined>();
    const [success, setSuccess] = useState<string | undefined>();
    const [detailsError, setDetailsError] = useState<string | undefined>();
    const [detailsSuccess, setDetailsSuccess] = useState<string | undefined>();
    const [validating, setValidating] = useState<boolean>(false);
    const [updatingCommon, setUpdatingCommon] = useState<boolean>(false);
    const [updatingDetails, setUpdatingDetails] = useState<boolean>(false);
    const [showMoreErrorInfo, setShowMoreErrorInfo] = useState<boolean>(false);
    useEffect(() => {
        const folioIdSplits = props.subExtensionFolioCompositeId?.split('_');
        if(!folioIdSplits || folioIdSplits.length != 3) {
            return;
        }
        setFolioId(folioIdSplits[1]);
        setSubExtensionId(folioIdSplits[2]);
        setError(undefined)
        setSuccess(undefined)
        setShowOpportunityDetails(false)
    }, [props.subExtensionFolioCompositeId]);
    
    useEffect(() => { 
        if(extensionFolioApi.data) {
            setCurrentFolio((current) => extensionFolioApi.data);
        }
    }, [extensionFolioApi.data]);

    useEffect(() => {
        if(currentFolio?.sourceExtensionFolio?.id && subExtensionsId) {
            if(!currentFolio.enabledSubExtensions?.includes(subExtensionsId)) {
                enableFolio({id: currentFolio.sourceExtensionFolio.id, subExtension: subExtensionsId})

                setCurrentFolio((existing) => ({...existing, enabledSubExtensions: [...existing?.enabledSubExtensions ?? [], subExtensionsId]}));
            }
        }
    }, [currentFolio, subExtensionsId]);


    const getOnValidate = (response: IValidateExtensionResponse) => {
        if (response.success) {
            setError(undefined);
            setValidationResponse(undefined)
            setSuccess('Extension settings validated');
            setShowOpportunityDetails(true)
        } else {
            setSuccess(undefined)
            setShowOpportunityDetails(false)
        }
        setValidationResponse(response)
        setValidating(false)
        };

    const getMessagebar = (validationResponse?: IValidateExtensionResponse) => {
        return(<div>
            {validationResponse && !validationResponse.success &&
                <MessageBar messageBarType={MessageBarType.error}>
                    {validationResponse.error}
                    {validationResponse.validationMessages && validationResponse.validationMessages.length > 0 && <Link onClick={() => {
                        setShowMoreErrorInfo(true);
                    }}>&nbsp;More Info</Link>}
                </MessageBar>
            }
            {validationResponse?.success &&
                <div>
                    {validationResponse.validationMessages && validationResponse.validationMessages.length > 0 ?
                        <MessageBar messageBarType={MessageBarType.warning} style={{paddingBottom: "10px"}}>
                            {validationResponse.message ?? 'Extension settings validated with warnings'}
                            {validationResponse.validationMessages && validationResponse.validationMessages.length > 0 && <Link onClick={() => {
                                setShowMoreErrorInfo(true);
                            }}>&nbsp;More Info</Link>}
                        </MessageBar>:
                        <MessageBar messageBarType={MessageBarType.success} style={{paddingBottom: "10px"}}>
                            {validationResponse.message ?? 'Extension settings validated'}
                        </MessageBar>
                    }

                </div>}
        </div>)
    }

    return (
        <>         
            {currentFolio && subExtensionsId &&
            <>
            <ExtensionValidationErrorModal errorDetails={validationResponse} displayModal={showMoreErrorInfo} closer={() => setShowMoreErrorInfo(false)} />

            <h3>Basic Integration Configuration</h3>
            {error && 
                <div style={{height: '2em'}}>
                    <MessageBar messageBarType={MessageBarType.error}>
                        {error}
                    </MessageBar>
                </div>
            }

            {getMessagebar(validationResponse)}

            <ExtensionFolioInstallForm
                onSetError={(error) => {
                    setError(error)
                }}
                onValidateStart={() => {setValidating(true)}}
                onValidateComplete={getOnValidate}
                folio={currentFolio}
                onUpdateStart={() => setUpdatingCommon(true)}
                onUpdateStop={() => setUpdatingCommon(false)}

            />
            <div style={{height: '2em'}}>
                {validating && <Spinner label='Validating' labelPosition='right' style={{display: 'inline-flex'}}/> }
                {updatingCommon && <Spinner label='Saving' labelPosition='right' style={{display: 'inline-flex'}}/> }
            </div>

            {showOpportunityDetails &&
            <div>
                <div style={{height: '2em'}}>
                    {detailsError && <MessageBar messageBarType={MessageBarType.error}>{detailsError}</MessageBar>}
                    {detailsSuccess && <MessageBar messageBarType={MessageBarType.success}>{detailsSuccess}</MessageBar>}
                </div>
                <SubExtensionInstallForm
                    key={`storefront-opp-subextension-install-${subExtensionsId}`}
                    folio={currentFolio}
                    subExtension={subExtensionsId}
                    onUpdateStart={() => setUpdatingDetails(true)}
                    onUpdateStop={() => setUpdatingDetails(false)}
                    onSetError={(error) => {
                        setDetailsError(error);
                        setDetailsSuccess(undefined);
                    }}
                />
                <div style={{height: '2em'}}>
                    {updatingDetails && <Spinner label='Saving' labelPosition='right' style={{display: 'inline-flex'}}/>}
                </div>
            </div>}</>
            }
        </>
    );
}

export default GlobalStorefrontOpportunityExtensionForm;